import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Pencil } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import QuillEditor from '../../../components/QuillEditor';
import RichContent, { normalizeRichText, richTextIsEmpty } from '../../../components/RichContent';
import AdminModal from '../../components/AdminModal';
import { OpenExternalIcon } from '../../../components/icons';
import {
  CONTENT_ORIGINS, FISCAL_CONTENT_FIELDS, provenanceLabel,
} from '../../../constants/ballotInitiatives';
import type { DeclarableField, ProvenanceEntry } from '../../../constants/ballotInitiatives';
import { formatDate } from '../../lib/format';
import { apiError, inputClasses, invalidateInitiativeQueries, labelClasses } from './InitiativeDetailPage';

/**
 * The Blue Book's fiscal presentation changes between cycles, which is why the detail
 * is JSONB rather than a rigid column set. These are the keys it uses today.
 */
const FISCAL_KEYS: Array<{ key: string; label: string; placeholder: string }> = [
  { key: 'state_revenue', label: 'State revenue', placeholder: 'e.g. Increases state revenue by $x million in FY 2027-28' },
  { key: 'state_spending', label: 'State spending', placeholder: 'e.g. Increases state spending by $x million' },
  { key: 'local_impact', label: 'Local government impact', placeholder: 'Effect on county and municipal budgets' },
  { key: 'tabor_refund', label: 'TABOR refund effect', placeholder: 'Effect on refunds under the revenue cap' },
  { key: 'fiscal_years', label: 'Fiscal years covered', placeholder: 'e.g. FY 2026-27 through FY 2029-30' },
  { key: 'notes', label: 'Notes', placeholder: 'Anything the categories above do not cover' },
  { key: 'additional_information', label: 'Additional information', placeholder: 'Context the Blue Book gives that the categories above miss' },
];

const KNOWN_KEYS = new Set(FISCAL_KEYS.map(f => f.key));

type Provenance = Partial<Record<DeclarableField, ProvenanceEntry>>;

interface Props {
  initiativeId: string;
  statement: string | null;
  detail: Record<string, string> | null;
  provenance: Provenance | null;
  /** The state's own fiscal impact statement, linked under the content when set. */
  fiscalImpactUrl: string | null;
}

/**
 * Fiscal impact — the short statement plus the structured breakdown.
 *
 * Both are drafted prose, exactly like the summary, so they carry the same
 * mandatory origin declaration (v2 PRD §5.6). The two fields are edited together
 * and declared together: one Origin control covers both, and one label renders
 * under the content. The Blue Book card holds the state's estimate verbatim with
 * its citation; the state's own document is linked from here as well.
 */
const InitiativeFiscalImpactCard: React.FC<Props> = ({
  initiativeId, statement, detail, provenance, fiscalImpactUrl,
}) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [statementDraft, setStatementDraft] = useState('');
  const [detailDraft, setDetailDraft] = useState<Record<string, string>>({});
  const [origin, setOrigin] = useState('');
  const [originDetail, setOriginDetail] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Keys the Blue Book has started using that this build does not know about yet.
  const unknownKeys = Object.keys(detail ?? {}).filter(k => !KNOWN_KEYS.has(k));

  // One declaration covers both fields; either entry carries it.
  const entry = provenance?.fiscal_impact_statement ?? provenance?.fiscal_impact_detail;
  const label = provenanceLabel(entry);

  const openEdit = () => {
    setStatementDraft(statement ?? '');
    const next: Record<string, string> = {};
    for (const { key } of FISCAL_KEYS) next[key] = detail?.[key] ?? '';
    setDetailDraft(next);
    // No fallback default: undeclared content opens with nothing selected, on purpose.
    setOrigin(entry?.origin ?? '');
    setOriginDetail(entry?.tool ?? entry?.source ?? '');
    setError(null);
    setOpen(true);
  };

  const filled = !richTextIsEmpty(statementDraft)
    || FISCAL_KEYS.some(({ key }) => !richTextIsEmpty(detailDraft[key]));
  const undeclared = filled && !origin;

  const saveMutation = useMutation({
    mutationFn: () => {
      // Merge over what is stored rather than replacing it: a key this build does not
      // render must survive an edit, or a Blue Book shape change silently drops data.
      const merged: Record<string, string> = { ...(detail ?? {}) };
      for (const { key } of FISCAL_KEYS) {
        const value = normalizeRichText(detailDraft[key]);
        if (value) merged[key] = value;
        else delete merged[key];
      }
      const values: Record<DeclarableField, unknown> = {
        fiscal_impact_statement: normalizeRichText(statementDraft) || null,
        fiscal_impact_detail: Object.keys(merged).length > 0 ? merged : null,
      } as Record<DeclarableField, unknown>;

      const declaration: Record<string, string> | null = origin ? { origin } : null;
      if (declaration && origin === 'ai_reviewed' && originDetail.trim()) declaration.tool = originDetail.trim();
      if (declaration && origin === 'official_source' && originDetail.trim()) declaration.source = originDetail.trim();

      // An emptied field drops its entry; a filled one carries the shared declaration.
      // With nothing chosen the patch stays silent and the server's mandatory rule
      // answers with the 400 that the modal then shows.
      const patch: Record<string, unknown> = {};
      for (const field of FISCAL_CONTENT_FIELDS) {
        if (values[field] == null) patch[field] = null;
        else if (declaration) patch[field] = declaration;
      }
      return api.put(`/admin/ballot-initiatives/${initiativeId}`, {
        fiscal_impact_statement: values.fiscal_impact_statement,
        fiscal_impact_detail: values.fiscal_impact_detail,
        content_provenance: patch,
      });
    },
    onSuccess: () => {
      toast.success('Fiscal impact saved');
      setOpen(false);
      invalidateInitiativeQueries(queryClient, initiativeId);
    },
    onError: (err: any) => setError(apiError(err, 'Failed to save fiscal impact')),
  });

  const populated = FISCAL_KEYS.filter(f => (detail?.[f.key] ?? '').trim() !== '');

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
          Fiscal Impact
        </h3>
        <Button variant="ghost" size="sm" icon={Pencil} onClick={openEdit}>Edit</Button>
      </div>

      {statement ? (
        <RichContent html={statement} className="text-sm text-text-primary dark:text-text-dark-primary" />
      ) : (
        <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">No fiscal statement recorded.</p>
      )}

      {populated.length > 0 && (
        <dl className="mt-4 space-y-2 border-t border-border-light pt-3 dark:border-border-dark">
          {populated.map(({ key, label: keyLabel }) => (
            <div key={key}>
              <dt className="text-xs font-medium text-text-secondary dark:text-text-dark-secondary">{keyLabel}</dt>
              <dd className="text-sm text-text-primary dark:text-text-dark-primary">
                <RichContent html={detail?.[key] ?? ''} className="text-sm text-text-primary dark:text-text-dark-primary" />
              </dd>
            </div>
          ))}
        </dl>
      )}

      {label && (
        <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          {label}
          {entry?.reviewed_at && ` · reviewed ${formatDate(entry.reviewed_at)}`}
        </p>
      )}

      {fiscalImpactUrl ? (
        <a
          href={fiscalImpactUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-sm text-primary-600 hover:underline dark:text-primary-400"
        >
          <OpenExternalIcon className="h-3 w-3" />
          Read the state&apos;s fiscal impact statement
        </a>
      ) : (
        <p className="mt-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          No state fiscal impact statement linked — add it under Links &amp; Resources.
        </p>
      )}

      {unknownKeys.length > 0 && (
        <p className="mt-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          Also stored, from a newer Blue Book shape: {unknownKeys.join(', ')}. These are kept
          on save but not editable here.
        </p>
      )}

      <AdminModal
        isOpen={open}
        onClose={() => setOpen(false)}
        title="Edit Fiscal Impact"
        size="lg"
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            The origin you choose becomes a public label — an AI-assisted or official-source
            fiscal impact is marked as such wherever citizens read it, and an unlabelled one is
            a claim that our team wrote it.
          </p>
          {error && (
            <p className="rounded-lg bg-danger-light px-3 py-2 text-sm text-danger dark:bg-danger-dark/20">{error}</p>
          )}
          {undeclared && (
            <p className="rounded-lg bg-warning-light px-3 py-2 text-sm text-warning-dark dark:bg-warning-dark/20">
              Choose where the fiscal impact text came from before saving.
            </p>
          )}
          <div>
            <span className={labelClasses}>Fiscal Impact Statement</span>
            <QuillEditor
              value={statementDraft}
              onChange={setStatementDraft}
              placeholder="Short inline fiscal-impact text, beyond the state's fiscal impact statement"
            />
          </div>

          <div className="border-t border-border-light pt-4 dark:border-border-dark">
            <p className="mb-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              Breakdown — leave a line empty to omit it.
            </p>
            {FISCAL_KEYS.map(({ key, label: keyLabel, placeholder }) => (
              <div key={key} className="mb-3">
                <span className={labelClasses}>{keyLabel}</span>
                <QuillEditor
                  value={detailDraft[key] ?? ''}
                  onChange={(value) => setDetailDraft((c) => ({ ...c, [key]: value }))}
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>

          {filled && (
            <fieldset className="border-t border-border-light pt-4 dark:border-border-dark">
              <legend className="mb-1.5 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Where did this text come from? *
              </legend>
              <p className="mb-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                One declaration covers the statement and the breakdown — they are edited as one piece.
              </p>
              <div className="space-y-1.5">
                {CONTENT_ORIGINS.map((option) => (
                  <label key={option.value} className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="origin-fiscal"
                      value={option.value}
                      checked={origin === option.value}
                      onChange={() => setOrigin(option.value)}
                      className="mt-1"
                    />
                    <span>
                      <span className="text-text-primary dark:text-text-dark-primary">{option.label}</span>
                      <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        {option.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              {(origin === 'ai_reviewed' || origin === 'official_source') && (
                <div className="mt-2">
                  <label htmlFor="detail-fiscal" className={labelClasses}>
                    {origin === 'ai_reviewed' ? 'Tool used' : 'Source'}
                  </label>
                  <input
                    id="detail-fiscal"
                    type="text"
                    maxLength={120}
                    value={originDetail}
                    onChange={(e) => setOriginDetail(e.target.value)}
                    className={inputClasses}
                    placeholder={origin === 'ai_reviewed' ? 'e.g. Claude Opus 5' : 'e.g. 2026 Blue Book, p. 12'}
                  />
                </div>
              )}
            </fieldset>
          )}
        </div>
      </AdminModal>
    </div>
  );
};

export default InitiativeFiscalImpactCard;
